import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FONT, COLOURS } from '../constants/theme';
import { COUPLE, CEREMONY, TIMELINE, NAV_ITEMS } from '../constants/weddingData';
import StatusBar     from '../components/ui/StatusBar';
import Dialog        from '../components/ui/Dialog';
import SectionHeader from '../components/ui/SectionHeader';
import PixelCard     from '../components/ui/PixelCard';
import PixelWindow   from '../components/ui/PixelWindow';
import BottomNav     from '../components/ui/BottomNav';
import TimelineRow   from '../components/ui/TimelineRow';
import RsvpForm      from '../components/forms/RsvpForm';
import CanadianFlag  from '../components/pixel/CanadianFlag';

const SITE = CEREMONY.toronto;

/**
 * TorontoSite — the Canada ceremony sub-website.
 *
 * Sub-pages: Home | Story | Details | RSVP
 * Navigating to the 'dock' key calls onBack() to return to DockScene.
 *
 * @param {object}   props
 * @param {function} props.onBack - Callback to return to the dock world-select.
 */
function TorontoSite({ onBack }) {
  const [page, setPage] = useState('home');

  const handleNav = (key) => {
    if (key === 'dock') { onBack(); return; }
    setPage(key);
  };

  return (
    <div
      className="fadeIn"
      style={{ fontFamily: FONT, background: COLOURS.cream, minHeight: '100vh' }}
    >
      <StatusBar
        left="🍁 TORONTO"
        mid="CANADA CEREMONY"
        right="♥♥♥♥♥"
        bg={SITE.accentColour}
      />

      {/* ════════════ HOME PAGE ════════════ */}
      {page === 'home' && (
        <>
          {/* Pixel art Toronto scene */}
          <svg
            width="100%"
            viewBox="0 0 380 252"
            style={{ display: 'block' }}
            role="img"
            aria-label="Toronto lakefront scene with condo buildings, CN Tower, maple trees, and boat arriving"
          >
            {/* ── SKY — bright midday ── */}
            <rect x="0"   y="0"   width="380" height="252" fill="#87CEEB" />
            <rect x="0"   y="0"   width="380" height="44"  fill="#5ab0d8" />
            {/* SUN — high in sky */}
            <rect x="305" y="10"  width="30"  height="30"  fill="#FFD060" />
            <rect x="301" y="14"  width="4"   height="22"  fill="#FFD060" />
            <rect x="335" y="14"  width="4"   height="22"  fill="#FFD060" />
            <rect x="309" y="6"   width="22"  height="4"   fill="#FFD060" />
            <rect x="309" y="40"  width="22"  height="4"   fill="#FFD060" />
            {/* CLOUDS */}
            {[[18,18],[198,14],[275,22]].map(([cx,cy], i) => (
              <g key={i}>
                <rect x={cx}   y={cy}   width="58" height="14" fill="#fff" opacity="0.92" />
                <rect x={cx-6} y={cy+7} width="70" height="14" fill="#fff" opacity="0.92" />
              </g>
            ))}

            {/* ── BUILDINGS ── */}
            {/* Glass condo tower — centrepiece */}
            <rect x="130" y="28"  width="64"  height="142" fill="#c8e0f5" />
            <rect x="132" y="30"  width="60"  height="138" fill="#d8ecff" />
            <rect x="132" y="32"  width="18"  height="136" fill="#e8f4ff" opacity="0.32" />
            {/* Window grid on glass tower */}
            {[[134,38],[150,38],[164,38],[134,56],[150,56],[164,56],
              [134,74],[150,74],[164,74],[134,92],[150,92],[164,92],
              [134,110],[150,110],[164,110],[134,128],[150,128],[164,128]
            ].map(([wx,wy], i) => (
              <rect key={i} x={wx} y={wy} width="8" height="8"
                fill={i % 3 === 1 ? '#fffacc' : '#a8d8f0'}
                opacity={i % 3 === 1 ? 0.8 : 1}
              />
            ))}
            <rect x="126" y="24"  width="72"  height="6"   fill="#b0c8d8" />
            <rect x="130" y="16"  width="4"   height="10"  fill="#909090" />
            <rect x="134" y="12"  width="5"   height="6"   fill="#90a8b8" />

            {/* Brick condo (right) */}
            <rect x="202" y="70"  width="54"  height="100" fill="#c87848" />
            {[82,96,110,124,138,152].map((by) => (
              <rect key={by} x="202" y={by} width="54" height="2" fill="#b86838" opacity="0.5" />
            ))}
            {[[208,78],[224,78],[238,78],[208,102],[224,102],[238,102],
              [208,126],[224,126],[238,126],[208,150],[224,150],[238,150]
            ].map(([wx,wy], i) => (
              <rect key={i} x={wx} y={wy} width="10" height="12"
                fill={i % 3 === 1 ? '#fffacc' : '#a8d8f0'}
                opacity={i % 3 === 1 ? 0.8 : 1}
              />
            ))}
            <rect x="198" y="66"  width="62"  height="6"   fill="#a86838" />

            {/* Far background buildings */}
            {[[0,64,34,106],[36,80,26,90],[64,54,36,116],[102,74,28,96],
              [258,82,30,88],[292,60,38,110],[332,90,26,80],[360,74,20,96]
            ].map(([bx,by,bw,bh], i) => (
              <rect key={i} x={bx} y={by} width={bw} height={bh}
                fill={i % 2 === 0 ? '#c0d0e0' : '#b0c8d8'}
              />
            ))}

            {/* CN Tower */}
            <rect x="108" y="40"  width="4"   height="100" fill="#a0b8c8" />
            <rect x="104" y="88"  width="12"  height="18"  fill="#b0c8d8" />
            <rect x="104" y="89"  width="12"  height="16"  fill="#c0d4e0" opacity="0.8" />
            <rect x="109" y="30"  width="2"   height="12"  fill="#909090" />

            {/* ── WATERFRONT ── */}
            <rect x="0"   y="170" width="380" height="10"  fill="#c0a878" />
            <rect x="0"   y="176" width="380" height="6"   fill="#a89060" />
            <rect x="0"   y="180" width="380" height="72"  fill="#7ab8d0" />
            <rect x="0"   y="170" width="380" height="4"   fill="#8a7050" />
            {/* Promenade railing posts */}
            {[10,30,50,70,90,272,292,312,332,352].map((rx) => (
              <rect key={rx} x={rx} y="163" width="4" height="9" fill="#a08060" />
            ))}

            {/* ── MAPLE TREES ── */}
            {[[14,10],[48,44]].map(([tx,dx], i) => (
              <g key={i}>
                <rect x={tx}      y="138" width="10"       height="34" fill="#8a5a28" />
                <rect x={tx-10}   y="108" width={28+dx/2}  height="42" fill="#d85a20" opacity="0.95" />
                <rect x={tx-6}    y="100" width={22+dx/3}  height="36" fill="#c84a18" opacity="0.9" />
                <rect x={tx-12}   y="130" width={34+dx/2}  height="22" fill="#d86030" opacity="0.7" />
              </g>
            ))}
            {[[330,10],[362,44]].map(([tx,dx], i) => (
              <g key={i}>
                <rect x={tx}      y="138" width="10"       height="34" fill="#8a5a28" />
                <rect x={tx-8}    y="108" width={28+dx/2}  height="42" fill="#d85a20" opacity="0.95" />
                <rect x={tx-4}    y="100" width="22"       height="36" fill="#c84a18" opacity="0.9" />
                <rect x={tx-10}   y="130" width="34"       height="22" fill="#d86030" opacity="0.7" />
              </g>
            ))}

            {/* Dock */}
            <rect x="158" y="170" width="64" height="42" fill="#c4a068" />
            {[177,185,193,201,209].map((py) => (
              <rect key={py} x="158" y={py} width="64" height="2" fill="#b09050" opacity="0.5" />
            ))}
            <rect x="155" y="170" width="70" height="4"  fill="#8a6830" />
            <rect x="155" y="208" width="70" height="4"  fill="#8a6830" />
            <rect x="155" y="170" width="4"  height="42" fill="#8a6830" />
            <rect x="221" y="170" width="4"  height="42" fill="#8a6830" />

            {/* ── TORONTO BOAT ARRIVING ── */}
            <g style={{ animation: 'boatL 3s ease-in-out infinite', transformOrigin: '120px 200px' }}>
              <rect x="76"  y="196" width="92"  height="28" fill="#c85a28" />
              <rect x="72"  y="192" width="100" height="7"  fill="#d86a38" />
              <rect x="68"  y="188" width="108" height="6"  fill="#e87848" />
              <rect x="76"  y="204" width="92"  height="4"  fill="#b84a18" />
              <polygon points="68,188 76,196 68,224" fill="#c85a28" />
              <rect x="76"  y="180" width="92"  height="10" fill="#e8d0a0" />
              <rect x="93"  y="156" width="54"  height="26" fill="#f0e0b8" />
              <polygon points="84,158 120,138 156,158" fill="#8a4a28" />
              <polygon points="86,158 120,140 154,158" fill="#a05a38" />
              <rect x="84"  y="156" width="72"  height="5"  fill="#6b3820" />
              <rect x="117" y="130" width="6"   height="30" fill="#8a6830" />
              {/* Canadian flag — animated */}
              <g style={{ animation: 'flagW 1.5s ease-in-out infinite' }} transform="translate(123,132)">
                <CanadianFlag x={0} y={0} w={22} h={14} />
              </g>
              <rect x="84"  y="172" width="72"  height="12" fill="#a84020" />
              <rect x="84"  y="172" width="72"  height="12" fill="none" stroke="#FFD060" strokeWidth="2" />
              <text x="120" y="181" textAnchor="middle" fontFamily={FONT} fontSize="5" fill="#FFD060">
                🍁 ARRIVING
              </text>
              <rect x="66"  y="220" width="118" height="4"  fill="#9ad8f0" opacity="0.5" />
            </g>

            {/* Water shimmer */}
            {[188,200,214,228].map((wy, i) => (
              <rect key={i} x={20 + i * 14} y={wy} width={50 + i * 10} height="2"
                fill="#9ad8f0" opacity="0.3"
                style={{ animation: `shim ${2 + i * 0.3}s ${i * 0.2}s ease-in-out infinite` }}
              />
            ))}

            {/* Falling leaves */}
            {[[115,140],[148,128],[242,136],[278,124],[60,148],[322,140]].map(([lx,ly], i) => (
              <rect key={i} x={lx} y={ly} width="7" height="6"
                fill={['#c84a18','#e07028','#d85a20'][i % 3]} opacity="0.85"
                style={{ animation: `leaf ${4 + i * 0.4}s ${i * 0.5}s ease-in infinite` }}
              />
            ))}

            {/* ── TITLE CARD — overlaid top-centre ── */}
            <rect x="112" y="4"   width="196" height="56" fill="#fff8f0" opacity="0.97" />
            <rect x="112" y="4"   width="196" height="56" fill="none" stroke="#c85a28" strokeWidth="3" />
            {[[110,2],[300,2],[110,52],[300,52]].map(([cx,cy], i) => (
              <rect key={i} x={cx} y={cy} width="10" height="10" fill="#c85a28" />
            ))}
            <text x="210" y="20"  textAnchor="middle" fontFamily={FONT} fontSize="5"  fill="#8a6848">
              🍁 The Wedding of
            </text>
            <text x="210" y="38"  textAnchor="middle" fontFamily={FONT} fontSize="12" fill="#c85a28">
              {COUPLE.displayNames}
            </text>
            <text x="210" y="52"  textAnchor="middle" fontFamily={FONT} fontSize="5"  fill="#c9843c">
              Toronto, Canada · TBD 2026
            </text>
          </svg>

          {/* Info cards */}
          <div style={{ padding: '10px 12px' }}>
            <SectionHeader>TORONTO CELEBRATION</SectionHeader>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              {SITE.cards.map((card) => (
                <PixelCard key={card.title} icon={card.icon} title={card.title} lines={card.lines} />
              ))}
            </div>
            <Dialog name="🐾 MATCHA [TORONTO]">
              &quot;I&apos;m officially your Toronto wedding dog. I wore my fancy collar.
              A goose tried to fight me by the lake — I won.&quot;
              <span className="cursor">█</span>
            </Dialog>
          </div>
        </>
      )}

      {/* ════════════ STORY PAGE ════════════ */}
      {page === 'story' && (
        <div style={{ padding: '10px 12px', overflowY: 'auto', maxHeight: '62vh' }}>
          <SectionHeader>OUR CHAPTERS</SectionHeader>
          {TIMELINE.map((entry, i) => (
            <TimelineRow key={i} {...entry} />
          ))}
          <Dialog name="🐾 MATCHA">
            &quot;Every chapter was perfect. Especially the ones with treats.&quot;
            <span className="cursor">█</span>
          </Dialog>
        </div>
      )}

      {/* ════════════ DETAILS PAGE ════════════ */}
      {page === 'details' && (
        <div style={{ padding: '10px 12px', overflowY: 'auto', maxHeight: '62vh' }}>
          <PixelWindow title="🍁 TORONTO VENUE">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
              <thead>
                <tr>
                  {['DETAIL','INFO'].map((h) => (
                    <th
                      key={h}
                      style={{
                        fontSize: 7, padding: '6px 8px',
                        border: `2px solid ${COLOURS.borderGold}`,
                        background: '#f5dbb8', color: '#8a3a18', textAlign: 'left',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SITE.details.map(([key, val], i) => (
                  <tr key={i}>
                    <td style={{ fontSize:7, padding:'6px 8px', border:`2px solid ${COLOURS.borderGold}`, color:COLOURS.textDark, background: i%2===0?COLOURS.cream:COLOURS.creamDark }}>{key}</td>
                    <td style={{ fontSize:7, padding:'6px 8px', border:`2px solid ${COLOURS.borderGold}`, color:COLOURS.primary, background: i%2===0?COLOURS.cream:COLOURS.creamDark }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PixelWindow>
          <SectionHeader>DRESS CODE</SectionHeader>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <PixelCard icon="🍂" title="COLOURS" lines={['Burgundy · Sage', 'Rust · Warm Gold', 'Forest Green']} />
            <PixelCard icon="🧥" title="WEATHER" lines={['Canadian autumn', 'Bring a warm layer', 'Eve can be crisp!']} />
          </div>
        </div>
      )}

      {/* ════════════ RSVP PAGE ════════════ */}
      {page === 'rsvp' && (
        <RsvpForm venue="🍁 TORONTO" accentColour={SITE.accentColour} />
      )}

      <BottomNav items={NAV_ITEMS.toronto} active={page} onNav={handleNav} />
    </div>
  );
}

TorontoSite.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default TorontoSite;
